import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, ShieldCheckIcon, ClockIcon, ZapIcon, RefreshCwIcon, LifeBuoyIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function MaintenanceService() {
  const features = [{
    icon: ShieldCheckIcon,
    title: 'Security Updates',
    description: 'Regular security patches and vulnerability fixes'
  }, {
    icon: RefreshCwIcon,
    title: 'Software Updates',
    description: 'Keep your CMS, plugins, and themes up to date'
  }, {
    icon: ZapIcon,
    title: 'Performance Optimization',
    description: 'Speed improvements and technical enhancements'
  }, {
    icon: LifeBuoyIcon,
    title: 'Priority Support',
    description: 'Fast response times when you need help'
  }, {
    icon: ClockIcon,
    title: 'Regular Backups',
    description: 'Automated daily backups for peace of mind'
  }, {
    icon: ShieldCheckIcon,
    title: 'Uptime Monitoring',
    description: '24/7 monitoring to catch issues early'
  }];
  const benefits = ['Prevent security breaches and hacks', 'Keep your site running fast and smooth', 'Avoid costly downtime and lost revenue', 'Focus on your business, not tech issues', 'Stay compliant with latest standards', 'Regular performance improvements'];
  const plans = [{
    name: 'Essential Care',
    price: '£79/month',
    features: ['Monthly security updates', 'Monthly backups', 'Basic performance monitoring', 'Email support (48hr response)', 'Uptime monitoring', 'SSL certificate management']
  }, {
    name: 'Total Growth',
    price: '£299/month',
    features: ['Weekly security updates', 'Daily backups', 'Advanced performance optimization', 'Priority support (24hr response)', '24/7 uptime monitoring', 'Monthly analytics reports', 'Content updates (1 hour/month)', 'Ongoing SEO improvements'],
    popular: true
  }];
  return <>
      <Helmet data-id="element-1923">
        <title data-id="element-1924">
          Website Maintenance Packages UK | Security & Updates – Bright Pixxel
        </title>
        <meta name="description" content="Professional website maintenance packages. Security updates, backups, performance optimization, and priority support from £79/month." data-id="element-1925" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-1926">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1927">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1928">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-1929">
              <ShieldCheckIcon size={40} className="text-[#0DC3DC]" data-id="element-1930" />
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-1931">
              Website Maintenance Services
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-1932">
              Keep your website secure, fast, and up-to-date with our
              professional maintenance services. We handle the technical details
              so you can focus on your business.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1933">
              <Link to="/website-care" className="bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-3 rounded-lg font-medium transition-colors shadow-lg" data-id="element-1934">
                View Care Plans
              </Link>
              <Link to="/contact" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#0C1B26] px-8 py-3 rounded-lg font-medium transition-colors" data-id="element-1935">
                Get Started
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Features Grid */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-1936">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1937">
          <div className="max-w-6xl mx-auto" data-id="element-1938">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-12" data-id="element-1939">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1940">
                Complete Website Care
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1941">
                Everything you need to keep your website running smoothly
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1942">
              {features.map((feature, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F9FAFB] dark:bg-[#212C34] p-6 rounded-xl hover:shadow-lg transition-shadow" data-id="element-1943">
                  <div className="w-12 h-12 bg-[#0DC3DC]/30 rounded-lg flex items-center justify-center mb-4 border border-[#0DC3DC]/40" data-id="element-1944">
                    <feature.icon size={24} className="text-[#0DC3DC]" data-id="element-1945" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" data-id="element-1946">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1947">
                    {feature.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Benefits Section with Image */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1948">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1949">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1950">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-1951">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1952">
                Why Regular Maintenance Matters
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-1953">
                Your website is a critical business asset. Regular maintenance
                protects your investment and ensures it continues to perform at
                its best.
              </p>
              <div className="space-y-4" data-id="element-1954">
                {benefits.map((benefit, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-center gap-3" data-id="element-1955">
                    <div className="w-6 h-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center flex-shrink-0" data-id="element-1956">
                      <CheckIcon size={14} className="text-[#0DC3DC]" data-id="element-1957" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300" data-id="element-1958">
                      {benefit}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="rounded-xl overflow-hidden shadow-xl" data-id="element-1959">
              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Website performance monitoring dashboard - Bright Pixxel Studios professional website maintenance services" className="w-full h-auto" loading="lazy" decoding="async" data-id="element-1960" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Plans Comparison */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-1961">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1962">
          <div className="max-w-5xl mx-auto" data-id="element-1963">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-1964">
              Choose Your Maintenance Package
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1965">
              {plans.map((plan, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className={`bg-[#F9FAFB] dark:bg-[#212C34] p-8 rounded-xl ${plan.popular ? 'ring-2 ring-[#0DC3DC] shadow-xl' : 'shadow-md'}`} data-id="element-1966">
                  {plan.popular && <div className="text-center mb-4" data-id="element-1967">
                      <span className="bg-[#0DC3DC] text-white px-4 py-1 rounded-full text-sm font-medium" data-id="element-1968">
                        Most Popular
                      </span>
                    </div>}
                  <h3 className="text-2xl font-bold mb-2" data-id="element-1969">{plan.name}</h3>
                  <div className="text-3xl font-bold text-[#0DC3DC] mb-6" data-id="element-1970">
                    {plan.price}
                  </div>
                  <ul className="space-y-3" data-id="element-1971">
                    {plan.features.map((feature, fIndex) => <li key={fIndex} className="flex items-start gap-3" data-id="element-1972">
                        <CheckIcon size={18} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1973" />
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-1974">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                  <Link to="/website-care" className={`mt-8 block text-center px-6 py-3 rounded-lg font-medium transition-colors ${plan.popular ? 'bg-[#0DC3DC] text-white hover:bg-[#0DC3DC]/90' : 'bg-white dark:bg-[#1E1E1E] border-2 border-[#0DC3DC] text-[#0DC3DC] hover:bg-[#0DC3DC] hover:text-white'}`} data-id="element-1975">
                    Learn More
                  </Link>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-1976">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1977">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1978">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6 text-white" data-id="element-1979">
              Protect Your Website Investment
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-white/90 mb-8" data-id="element-1980">
              Get started with our maintenance services today and enjoy peace of
              mind knowing your website is in expert hands.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-1981">
              <a href="mailto:info@bright-pixxel.com?subject=Maintenance%20Service%20Enquiry" className="inline-block bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-4 rounded-lg font-bold transition-colors shadow-lg text-lg" data-id="element-1982">
                Start Your Care Plan
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}